import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';
import { DataService } from '../../data.service';

// NAPLO – Dokumentumtár: az ügyfél összes feltöltött PDF-je egy helyen. ZIP-et is fel lehet tölteni, amit a rendszer
// kibont és automatikusan párosít a könyvelési tételekkel (a tétel számlaszámát keresve a PDF szövegében).
// A párosítatlan dokumentumokat egy kereshető felületen lehet kézzel a megfelelő tételhez rendelni.
@Component({
  selector: 'app-naplo-dokumentumtar',
  templateUrl: './dokumentumtar.component.html',
  styleUrls: ['../naplo-shared.scss', './dokumentumtar.component.scss'],
})
export class DokumentumtarComponent implements OnInit {
  @ViewChild('parosModal') parosModal: IonModal;
  @ViewChild('konyvModal') konyvModal: IonModal;
  @ViewChild('konyvSzlaValaszto') konyvSzlaValaszto: IonModal;
  @ViewChild('partnerPicker') partnerPicker: IonModal;
  get aktiv() { return this.naplo.aktiv; }
  dokumentumok: any[] = [];
  szuro: 'mind' | 'parositott' | 'parositlan' = 'mind';
  kereses = '';                                          // szabadszavas szűrő (fájlnév, számlaszám, partner…)
  evSzuro: number | 'mind' = new Date().getFullYear();   // alapból a könyvelt évre szűr
  feltoltes = false;

  // manuális párosító
  parosDoc: any = null;
  tetelTalalat: any[] = [];
  tetelKeres = '';

  // email-elkapó (decentralizált): beérkezett levelek + globális beállítások
  emailek: any[] = [];
  emailBeall: any = null;
  emailCim = '';
  emailPanel = false;
  emailRegFut = false;
  felismerFut: string | null = null;   // épp futó felismerés dokumentum-id-ja

  constructor(private naplo: NaploService, private toast: ToastController, private alert: AlertController, private http: HttpClient, private data: DataService) {}

  // Csak kettős könyvelésű ügyfélnél van értelme (a kettős bizonylat-szerkesztőt nyitja).
  get kettos() { return this.aktiv.konyveles_mod === 'kettos'; }

  /* ===== Közvetlen kettős könyvelés a dokumentumtárból (helyben, navigáció nélkül) ===== */
  // A /api/kettos backend modellt használjuk: fix FŐLÁB (szállító 4541) + soronkénti T/K lábak, devizánál
  // MNB-középárfolyammal HUF-ra váltva. Főleg az OSA-ból le nem jövő devizás számlákhoz.
  private readonly kbase = '/api/kettos';
  konyvSzerk: any = null;          // a szerkesztett bizonylat
  konyvDok: any = null;            // a forrás-dokumentum
  konyvTukor: any[] = [];          // számlatükör (név + választó + gyűjtő-szűrés)
  konyvBeallitas: any = null;      // a fix lábak alapértékeihez
  konyvJavaslatok: any[] = [];     // tanult ellenszámla-javaslatok
  konyvMentFut = false;
  konyvBetolt = false;             // épp olvassuk ki a dokumentum adatait
  penznemek = ['EUR', 'USD', 'GBP', 'CHF', 'RON', 'PLN', 'CZK', 'SEK', 'NOK', 'DKK'];

  private kget<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.kbase + p)); }
  private kpost<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.kbase + p, b)); }

  // „Könyvelés" egy dokumentumon: kiolvassuk a bizonylatszámot/partnert/végösszeget/devizát a PDF-ből,
  // betöltjük a számlatükröt + beállításokat + kontír-javaslatot, majd helyben megnyitjuk a szerkesztőt.
  // Meglévő (T/K) sorokból fix FŐLÁB + soronkénti lábak – a kettős szerkesztő modellje.
  private bontFixLab(teljes: any[], naploTipus: string) {
    const fix = this.foszamlaAlap(naploTipus);
    let idx = teljes.findIndex((s) => String(s.szamlaszam) === String(fix));
    if (idx < 0) idx = teljes.findIndex((s) => String(s.szamlaszam).startsWith(naploTipus === 'vevo' ? '3' : '4')
      && (naploTipus === 'szallito' ? (Number(s.kovetel) || 0) > 0 : (Number(s.tartozik) || 0) > 0));
    const sor = (s: any) => ({ szamlaszam: s.szamlaszam, megnevezes: s.megjegyzes || '', osszeg: (Number(s.tartozik) || 0) || (Number(s.kovetel) || 0) || null, afa_kulcs: s.afa_kulcs, oldal: (Number(s.tartozik) || 0) > 0 ? 'T' : 'K' });
    if (idx >= 0) return { foszamla: teljes[idx].szamlaszam, sorok: teljes.filter((_, i) => i !== idx).map(sor) };
    return { foszamla: fix || '', sorok: teljes.map(sor) };
  }

  // A dokumentumhoz már tartozik kettős bizonylat? Akkor AZT nyitjuk meg módosításra (id-vel),
  // különben a mentés új bizonylatot hozna létre a meglévő módosítása helyett.
  private async meglevoBizonylat(d: any) {
    if (!d?.tetel_id) return null;
    try { const t: any = await this.kget(`/tetel/${d.tetel_id}`); return t?.id ? t : null; }
    catch { return null; }   // nem kettős tétel (pl. egyszeres napló) – új könyvelés
  }

  async konyvelDok(d: any) {
    this.konyvDok = d;
    this.konyvBetolt = true;
    // 1) már könyvelt dokumentum → a MEGLÉVŐ bizonylat betöltése (PDF-újraolvasás nélkül)
    try {
      const meglevo = await this.meglevoBizonylat(d);
      if (meglevo) {
        try { this.konyvTukor = await this.kget(`/szamlatukor/${this.aktiv.ugyfelId}`); } catch { /* marad */ }
        try { this.konyvBeallitas = await this.kget(`/beallitas/${this.aktiv.ugyfelId}`); } catch { /* marad */ }
        await this.partnerekBetolt();
        const fl = this.bontFixLab(meglevo.sorok || [], meglevo.naplo_tipus);
        const nap = (v: any) => (v ? String(v).slice(0, 10) : null);
        // A sorok HUF-ban vannak tárolva; devizás bizonylatnál VISSZAVÁLTJUK devizára, mert a
        // szerkesztő devizában dolgozik és mentéskor újra HUF-ra vált (különben duplán szorozna).
        const arf = meglevo.deviza && Number(meglevo.arfolyam) > 0 ? Number(meglevo.arfolyam) : 0;
        const sorok = (fl.sorok.length ? fl.sorok : [this.uresKonyvSor()]).map((s: any) =>
          (arf ? { ...s, osszeg: s.osszeg == null ? null : Math.round((Number(s.osszeg) / arf) * 100) / 100 } : s));
        this.konyvSzerk = { ...meglevo,
          datum: nap(meglevo.datum), teljesites: nap(meglevo.teljesites), afa_datum: nap(meglevo.afa_datum), hatarido: nap(meglevo.hatarido),
          devizas: !!meglevo.deviza, deviza: meglevo.deviza || 'EUR', arfolyam: meglevo.arfolyam != null ? Number(meglevo.arfolyam) : null,
          foszamla: fl.foszamla, sorok, dok_id: d.id };
        this.konyvJavaslatok = [];
        await this.forditBetolt(meglevo.bizonylatszam);
        this.konyvBetolt = false;
        await this.konyvModal.present();
        return;
      }
    } catch { /* nem baj – megyünk az új könyvelés útján */ }

    let adat: any = {};
    try { adat = await this.naplo.post('/dokumentum/' + d.id + '/konyveles-adat', {}); }
    catch { adat = {}; }
    finally { this.konyvBetolt = false; }
    try { this.konyvTukor = await this.kget(`/szamlatukor/${this.aktiv.ugyfelId}`); } catch { this.konyvTukor = this.konyvTukor || []; }
    try { this.konyvBeallitas = await this.kget(`/beallitas/${this.aktiv.ugyfelId}`); } catch { /* marad */ }

    await this.partnerekBetolt();

    const ma = new Date(this.aktiv.ev, new Date().getMonth(), new Date().getDate());
    const nap = (v: any) => (v ? String(v).slice(0, 10) : null);
    const datum = nap(adat.datum || d.szamla_datum) || this.datStr(ma);
    // NÉGY dátum a PDF-ből: kelt / teljesítés / ÁFA-teljesítés / fizetési határidő
    const kelt = nap(adat.kelt) || datum;
    const teljesites = nap(adat.teljesites) || datum;
    const deviza = adat.deviza || 'HUF';
    const devizas = !!deviza && deviza !== 'HUF';
    const partner = adat.partner || d.partner || '';
    const brutto = adat.brutto != null ? Number(adat.brutto) : null;
    const netto = adat.netto != null ? Number(adat.netto) : null;
    const afa = adat.afa != null ? Number(adat.afa) : null;
    this.konyvSzerk = {
      naplo_tipus: 'szallito', datum: kelt, teljesites,
      afa_datum: nap(adat.afa_datum) || teljesites, hatarido: nap(adat.hatarido),
      bizonylatszam: adat.szamlaszam || d.szamlaszam || '',
      partner_nev: partner, partner_adoszam: adat.partner_adoszam || d.partner_adoszam || '',
      partner_id: adat.partner_id || this.partnerIllesztes(partner, adat.partner_adoszam || d.partner_adoszam)?.id || null,
      megnevezes: 'Beszerzés – ' + (partner || adat.szamlaszam || d.szamlaszam || ''),
      foszamla: this.foszamlaAlap('szallito', devizas),
      // Ha a PDF-ből kijött a nettó/ÁFA bontás, a nettó megy a költség-sorba (az ÁFA-sort a
      // kontír-javaslat betöltése után fűzzük hozzá a levonható ÁFA számlájával). Bontás nélkül
      // a végösszeg kerül egyetlen sorba, amit a felhasználó bonthat. A főláb auto-egyenlít.
      sorok: [{ szamlaszam: '', megnevezes: '', osszeg: netto != null ? netto : brutto, oldal: 'T', afa_kulcs: adat.afa_kulcs != null ? Number(adat.afa_kulcs) : null }],
      dok_id: d.id, devizas, deviza: devizas ? deviza : 'EUR', arfolyam: null,
    };
    await this.kontirJavaslatBetolt();   // ez tölti be a konyvAfas / konyvAfaSzamla értéket is
    // Levonható ÁFA külön sorban – CSAK áfaalany ügyfélnél. Ha az ügyfél nem áfás (adószám
    // középső jegye 1), az ÁFA nem levonható: a költséget BRUTTÓBAN, egy sorban könyveljük.
    if (!this.konyvAfas) {
      if (brutto != null) this.konyvSzerk.sorok[0].osszeg = brutto;
    } else if (netto != null && afa) {
      this.konyvSzerk.sorok.push({ szamlaszam: this.konyvAfaSzamla || '466', megnevezes: 'Levonható ÁFA', osszeg: afa, oldal: 'T', afa_kulcs: adat.afa_kulcs != null ? Number(adat.afa_kulcs) : null });
    }
    this.afaSeged = { alap: Number(this.konyvSzerk.sorok[0]?.osszeg) || null, kulcs: adat.afa_kulcs != null ? Number(adat.afa_kulcs) || 27 : 27, fordit: false };
    if (devizas) await this.arfolyamFrissit();
    if (brutto == null) this.ok('Bizonylatszám/partner kiolvasva. A végösszeget nem találtam – add meg kézzel.');
    else if (!this.konyvAfas) this.ok(`Kiolvasva: ${brutto} ${deviza} (bruttó – az ügyfél nem áfaalany, nincs levonható ÁFA).`);
    else if (netto != null && afa) this.ok(`Kiolvasva: nettó ${netto} + ÁFA ${afa} = ${brutto} ${deviza}.`);
    await this.konyvModal.present();
  }

  /* ===== Partner-választó a törzsből (a kettőssel azonos logika) ===== */
  partnerek: any[] = [];
  partnerKereso = '';
  async partnerekBetolt(eros = false) {
    if (!this.aktiv.ugyfelId || (!eros && this.partnerek.length)) return;
    try { this.partnerek = await this.naplo.get(`/partnerek/${this.aktiv.ugyfelId}`); } catch { /* marad */ }
  }
  get szurtPartnerek() {
    const k = this.partnerKereso.trim().toLowerCase();
    if (!k) return this.partnerek.slice(0, 80);
    return this.partnerek.filter((p: any) => String(p.nev || '').toLowerCase().includes(k)
      || String(p.rovid_nev || '').toLowerCase().includes(k)
      || String(p.adoszam || '').replace(/\D/g, '').includes(k.replace(/\D/g, '') || ' ')).slice(0, 80);
  }
  async partnerPickerNyit() { this.partnerKereso = ''; await this.partnerekBetolt(); this.partnerPicker.present(); }
  partnerValaszt(p: any) {
    this.konyvSzerk.partner_id = p.id;
    this.konyvSzerk.partner_nev = p.rovid_nev || p.nev;
    this.konyvSzerk.partner_adoszam = p.adoszam || null;
    this.partnerPicker.dismiss();
    this.kontirJavaslatBetolt();
  }
  partnerTorles() { this.konyvSzerk.partner_id = null; this.konyvSzerk.partner_nev = ''; this.konyvSzerk.partner_adoszam = null; }
  partnerIllesztes(nev: string, adoszam?: string) {
    const ado = String(adoszam || '').replace(/\D/g, '').slice(0, 8);
    const nk = String(nev || '').trim().toLowerCase();
    if (ado.length === 8) {
      const p = this.partnerek.find((x: any) => String(x.adoszam || '').replace(/\D/g, '').slice(0, 8) === ado);
      if (p) return p;
    }
    return this.partnerek.find((x: any) => String(x.nev || '').trim().toLowerCase() === nk
      || String(x.rovid_nev || '').trim().toLowerCase() === nk) || null;
  }
  get partnerHianyzik() { return !!(this.konyvSzerk?.partner_nev && !this.konyvSzerk?.partner_id && !this.partnerIllesztes(this.konyvSzerk.partner_nev, this.konyvSzerk.partner_adoszam)); }
  async partnerFelvesz(nevBe?: string) {
    const nev = String(nevBe || this.konyvSzerk?.partner_nev || '').trim();
    if (!nev) return;
    try {
      const r: any = await this.naplo.post('/partner', { ugyfel_id: this.aktiv.ugyfelId, nev, adoszam: (nevBe ? null : this.konyvSzerk.partner_adoszam) || null });
      await this.partnerekBetolt(true);
      const p = this.partnerek.find((x: any) => x.id === r?.id) || this.partnerIllesztes(nev);
      if (nevBe && p) this.partnerValaszt(p);
      else this.konyvSzerk.partner_id = p?.id || null;
      this.ok('Partner felvéve a törzsbe.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült felvenni a partnert.'); }
  }

  private datStr(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
  // A napló fix lába (szállító→4541) a beállításokból; DEVIZÁS bizonylatnál a deviza-partnerszámla
  // (RLB: 317 külföldi követelés / 4542 külföldi szállító), ha az ügyfélnél be van állítva.
  foszamlaAlap(t: string, devizas = false) {
    const b = this.konyvBeallitas || {};
    if (devizas) { const d = ({ vevo: b.deviza_vevo_szamla, szallito: b.deviza_szallito_szamla } as any)[t]; if (d) return d; }
    return ({ vevo: b.vevo_szamla, szallito: b.szallito_szamla, penztar: b.penztar_szamla, bank: b.bank_szamla } as any)[t] || (t === 'szallito' ? '4541' : '');
  }
  alapSorOldal() { return this.konyvSzerk?.naplo_tipus === 'vevo' ? 'K' : 'T'; }
  uresKonyvSor() { return { szamlaszam: '', megnevezes: '', osszeg: null, afa_kulcs: null, oldal: this.alapSorOldal() }; }
  sorHozzaad() { this.konyvSzerk.sorok.push(this.uresKonyvSor()); }
  sorTorol(i: number) { this.konyvSzerk.sorok.splice(i, 1); }

  konyvAfaSzamla = '';   // levonható ÁFA főkönyvi száma (a beállításokból, a kontír-javaslattal jön)
  konyvAfas = true;      // áfaalany-e az ügyfél (adószám középső jegye ≠ 1)
  async kontirJavaslatBetolt() {
    try {
      const j: any = await this.kpost(`/kontir-javaslat/${this.aktiv.ugyfelId}`, { naplo_tipus: this.konyvSzerk.naplo_tipus, partner_nev: this.konyvSzerk.partner_nev, megnevezes: this.konyvSzerk.megnevezes });
      this.konyvJavaslatok = j.javaslatok || [];
      this.konyvAfaSzamla = j.afa_elozetes || this.konyvAfaSzamla;
      if (j.afas != null) this.konyvAfas = !!j.afas;
      // ha az első sornak még nincs számlája, beírjuk a legjobb javaslatot
      const sor = this.konyvSzerk.sorok[0];
      if (sor && !sor.szamlaszam && this.konyvJavaslatok[0]) { sor.szamlaszam = this.konyvJavaslatok[0].szamlaszam; if (this.konyvJavaslatok[0].afa_kulcs != null) sor.afa_kulcs = this.konyvJavaslatok[0].afa_kulcs; }
    } catch { this.konyvJavaslatok = []; }
  }
  javaslatAlkalmaz(j: any) {
    let sor = this.konyvSzerk.sorok.find((s: any) => !s.szamlaszam);
    if (!sor) { sor = this.uresKonyvSor(); this.konyvSzerk.sorok.push(sor); }
    sor.szamlaszam = j.szamlaszam; if (j.afa_kulcs != null) sor.afa_kulcs = j.afa_kulcs;
  }

  /* ===== ÁFA-segéd (fordított adózás is) – a kettős kontírozóval azonos backend-logika =====
     FONTOS: a FORDÍTOTT adó ÖNÁLLÓ bizonylat – nem a szállítói sorral áll szemben (a szállítónak
     a nettó jár, az adót az adóhatóság felé önadózzuk). Ezért a fordított ÁFA sorait NEM fűzzük
     ehhez a bizonylathoz, hanem mentéskor külön (vegyes naplós) bizonylatot készítünk belőlük. */
  afaSeged: { alap: number | null; kulcs: number; fordit: boolean } = { alap: null, kulcs: 27, fordit: false };
  forditSorok: any[] = [];       // a külön bizonylatra váró fordított ÁFA sorok
  forditAfa = 0;
  forditMeglevo = false;         // az ÁFA-bizonylat MÁR létezik (visszanyitáskor) – ne készüljön újra
  // Meglévő bizonylat megnyitásakor: van-e már fordított ÁFA bizonylata („<bizonylatszám> ÁFA")?
  private async forditBetolt(bizonylatszam: string) {
    this.forditTorol();
    const biz = String(bizonylatszam || '').trim();
    if (!biz) return;
    try {
      const t: any = await this.kget(`/tetel-bizonylat/${this.aktiv.ugyfelId}?biz=${encodeURIComponent(biz + ' ÁFA')}`);
      if (!t || !t.sorok?.length) return;
      this.forditSorok = t.sorok.map((s: any) => ({ szamlaszam: s.szamlaszam, tartozik: Number(s.tartozik) || 0, kovetel: Number(s.kovetel) || 0, afa_kulcs: s.afa_kulcs, megjegyzes: s.megjegyzes }));
      this.forditAfa = this.forditSorok.reduce((m: number, s: any) => Math.max(m, s.tartozik, s.kovetel), 0);
      this.forditMeglevo = true;
    } catch { /* nincs ÁFA-bizonylat – marad üres */ }
  }
  async afaSorokHozzaad() {
    if (!this.afaSeged.alap) return this.hiba('Add meg az ÁFA alapját (a nettó összeget).');
    const irany = this.konyvSzerk.naplo_tipus === 'vevo' ? 'ertekesites' : 'beszerzes';
    // A FORDÍTOTT adó önálló, FORINTOS bizonylatra megy → az alapot előbb forintosítjuk, és
    // forintban (egész összegre) számoljuk. Az egyenes ÁFA a bizonylat pénznemében marad
    // (devizánál nincs kerekítés – a forintosítás a bizonylat mentésekor történik).
    const devizasBiz = !!(this.konyvSzerk.devizas && Number(this.konyvSzerk.arfolyam) > 0);
    const alap = this.afaSeged.fordit && devizasBiz ? this.hufErtek(this.afaSeged.alap) : Number(this.afaSeged.alap);
    const deviza = this.afaSeged.fordit ? 'HUF' : (devizasBiz ? this.konyvSzerk.deviza : 'HUF');
    try {
      const r: any = await this.kpost(`/afa-sorok/${this.aktiv.ugyfelId}`, { alap, kulcs: this.afaSeged.kulcs, fordit: this.afaSeged.fordit, irany, deviza });
      if (!r.sorok?.length) return this.hiba(r.afas ? 'Nincs generálható ÁFA-sor.' : 'Nem áfaalany cégnél egyenes ÁFA-sor nincs – az adó a beszerzési ár része.');
      if (r.kulon_bizonylat) {
        this.forditSorok = r.sorok; this.forditAfa = r.afa;
        this.ok(`Fordított ÁFA ${r.afa} – külön bizonylatként készül el mentéskor.`);
      } else {
        for (const s of r.sorok) {
          this.konyvSzerk.sorok.push({ szamlaszam: s.szamlaszam, megnevezes: s.megjegyzes || '', osszeg: (Number(s.tartozik) || 0) || (Number(s.kovetel) || 0) || null, afa_kulcs: s.afa_kulcs, oldal: (Number(s.tartozik) || 0) > 0 ? 'T' : 'K' });
        }
        this.ok(`ÁFA-sorok hozzáadva (${r.afa}).`);
      }
      this.afaSeged.alap = null;
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült ÁFA-sorokat generálni.'); }
  }
  forditTorol() { this.forditSorok = []; this.forditAfa = 0; this.forditMeglevo = false; }
  // A fordított ÁFA önálló bizonylata (vegyes napló) – a számla bizonylata után mentve.
  private async forditBizonylatMent() {
    if (!this.forditSorok.length || this.forditMeglevo) return;   // már létezik – ne duplikáljuk
    const d = this.konyvSzerk.afa_datum || this.konyvSzerk.teljesites || this.konyvSzerk.datum;
    await this.kpost(`/tetel/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`, {
      naplo_tipus: 'vegyes', datum: d, teljesites: d, afa_datum: d,
      bizonylatszam: (this.konyvSzerk.bizonylatszam || '') + ' ÁFA',
      partner_nev: this.konyvSzerk.partner_nev, partner_id: this.konyvSzerk.partner_id,
      megnevezes: 'Fordított ÁFA – ' + (this.konyvSzerk.partner_nev || this.konyvSzerk.bizonylatszam || ''),
      sorok: this.forditSorok.map((s: any) => ({ szamlaszam: s.szamlaszam, tartozik: Number(s.tartozik) || 0, kovetel: Number(s.kovetel) || 0, afa_kulcs: s.afa_kulcs || null, megjegyzes: s.megjegyzes || null })),
    });
  }

  // egyensúly (a kettős editor logikája): minden sornak saját T/K oldala, a FŐLÁB a kiegyenlítő
  private oldalSum(oldal: 'T' | 'K') { return Math.round((this.konyvSzerk?.sorok || []).reduce((s: number, x: any) => s + (((x.oldal || this.alapSorOldal()) === oldal) ? (Number(x.osszeg) || 0) : 0), 0) * 100) / 100; }
  get foOsszeg() { return Math.abs(Math.round((this.oldalSum('T') - this.oldalSum('K')) * 100) / 100); }
  get foOldal() { return this.oldalSum('T') >= this.oldalSum('K') ? 'K' : 'T'; }
  get tSum() { return Math.round((this.oldalSum('T') + (this.foOldal === 'T' ? this.foOsszeg : 0)) * 100) / 100; }
  get kSum() { return Math.round((this.oldalSum('K') + (this.foOldal === 'K' ? this.foOsszeg : 0)) * 100) / 100; }
  get egyensulyban() {
    if (!this.konyvSzerk?.foszamla || this.foOsszeg <= 0) return false;
    return this.konyvSzerk.sorok.every((s: any) => s.szamlaszam && (Number(s.osszeg) || 0) > 0 && (s.oldal === 'T' || s.oldal === 'K')) && this.tSum === this.kSum;
  }
  // deviza-előnézet: HUF érték az MNB-árfolyammal
  hufErtek(devOsszeg: any) { const a = this.konyvSzerk?.devizas && Number(this.konyvSzerk?.arfolyam) > 0 ? Number(this.konyvSzerk.arfolyam) : 1; return Math.round((Number(devOsszeg) || 0) * a * 100) / 100; }
  // a backend teljes T/K sorai: minden sor a saját oldalán + FŐLÁB; devizánál HUF-ra váltva (kerekítés-biztos)
  private epitSorok() {
    const arf = this.konyvSzerk.devizas && Number(this.konyvSzerk.arfolyam) > 0 ? Number(this.konyvSzerk.arfolyam) : 1;
    const huf = (v: any) => Math.round((Number(v) || 0) * arf * 100) / 100;
    const out = this.konyvSzerk.sorok.filter((s: any) => s.szamlaszam && (Number(s.osszeg) || 0) > 0).map((s: any) => {
      const oldal = s.oldal || this.alapSorOldal(); const v = huf(s.osszeg);
      return { szamlaszam: String(s.szamlaszam).trim(), tartozik: oldal === 'T' ? v : 0, kovetel: oldal === 'K' ? v : 0, afa_kulcs: s.afa_kulcs || null, megjegyzes: s.megnevezes || null };
    });
    const tS = Math.round(out.reduce((a, b) => a + b.tartozik, 0) * 100) / 100;
    const kS = Math.round(out.reduce((a, b) => a + b.kovetel, 0) * 100) / 100;
    const foHuf = Math.abs(Math.round((tS - kS) * 100) / 100);
    const foOldal = tS >= kS ? 'K' : 'T';
    out.push({ szamlaszam: String(this.konyvSzerk.foszamla).trim(), tartozik: foOldal === 'T' ? foHuf : 0, kovetel: foOldal === 'K' ? foHuf : 0, afa_kulcs: null, megjegyzes: this.konyvSzerk.megnevezes || null });
    return out;
  }
  async arfolyamFrissit() {
    if (!this.konyvSzerk?.devizas || !this.konyvSzerk?.deviza) return;
    const datum = (this.konyvSzerk.datum || new Date().toISOString().slice(0, 10)).slice(0, 10);
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/naplo-bank/mnb?deviza=${encodeURIComponent(this.konyvSzerk.deviza)}&datum=${datum}`));
      if (r?.arfolyam) this.konyvSzerk.arfolyam = Math.round(Number(r.arfolyam) * 10000) / 10000;
    } catch { this.hiba('MNB árfolyam nem elérhető – add meg kézzel.'); }
  }
  devizaValt() { if (this.konyvSzerk.devizas && !this.konyvSzerk.deviza) this.konyvSzerk.deviza = 'EUR'; if (this.konyvSzerk.devizas) this.arfolyamFrissit(); }

  // főkönyvi szám-választó a törzsből (gyűjtőre nem könyvelünk)
  konyvSzlaCel: any = null; konyvSzlaKereso = '';
  szamlaNev(szs: string) { const s = this.konyvTukor.find((x) => x.szamlaszam === szs); return s ? s.nev : ''; }
  nyitKonyvSzla(cel: any) { this.konyvSzlaCel = cel; this.konyvSzlaKereso = ''; this.konyvSzlaValaszto.present(); }
  get konyvSzlaTalalat() {
    const qy = this.konyvSzlaKereso.trim().toLowerCase();
    const lista = (this.konyvTukor || []).filter((x: any) => !x.gyujto);
    if (!qy) return lista.slice(0, 60);
    return lista.filter((x: any) => String(x.szamlaszam).includes(qy) || String(x.nev || '').toLowerCase().includes(qy)).slice(0, 60);
  }
  valasztKonyvSzla(x: any) {
    if (this.konyvSzlaCel?.fo) this.konyvSzerk.foszamla = x.szamlaszam;
    else if (this.konyvSzlaCel?.sor) this.konyvSzlaCel.sor.szamlaszam = x.szamlaszam;
    this.konyvSzlaValaszto.dismiss();
  }

  async konyvMentes() {
    if (!this.konyvSzerk?.foszamla) return this.hiba('Add meg a fő számlát (a napló fix lábát).');
    if (!this.egyensulyban) return this.hiba(`A tétel nincs egyensúlyban (T ${this.tSum} ≠ K ${this.kSum}).`);
    this.konyvMentFut = true;
    try {
      const modosit = !!this.konyvSzerk.id;
      const forditVolt = this.forditSorok.length > 0;
      await this.kpost(`/tetel/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`, { ...this.konyvSzerk, sorok: this.epitSorok() });
      await this.forditBizonylatMent();   // a fordított ÁFA ÖNÁLLÓ bizonylata (ha kértük)
      this.forditTorol();
      await this.konyvModal.dismiss(); await this.betolt();
      this.ok((modosit ? 'Bizonylat módosítva.' : 'Bizonylat könyvelve, dokumentum párosítva.') + (forditVolt ? ' A fordított ÁFA külön bizonylatra került.' : ''));
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba a könyvelésnél.'); }
    finally { this.konyvMentFut = false; }
  }

  async ngOnInit() { await this.naplo.init(); this.evSzuro = this.aktiv.ev; if (this.aktiv.ugyfelId) { this.betolt(); this.emailekBetolt(); this.emailBeallBetolt(); this.emailCimBetolt(); } }

  // ----- email-elkapó -----
  async emailekBetolt() { try { this.emailek = await this.naplo.get('/email?ugyfelId=' + this.aktiv.ugyfelId); } catch { this.emailek = []; } }
  async emailCimBetolt() { try { const r: any = await this.naplo.get('/email/cim/' + this.aktiv.ugyfelId); this.emailCim = r?.cim || ''; } catch { this.emailCim = ''; } }
  async emailBeallBetolt() { try { this.emailBeall = await this.naplo.get('/email/beallitas'); } catch { this.emailBeall = {}; } }
  async emailBeallMent() {
    try { await this.naplo.put('/email/beallitas', this.emailBeall || {}); await this.emailBeallBetolt(); this.ok('Email-beállítások mentve.'); }
    catch { this.hiba('Nem sikerült menteni.'); }
  }
  async emailRegisztracio() {
    this.emailRegFut = true;
    try { const r: any = await this.naplo.post('/email/regisztracio', {}); this.ok(`Regisztrálva: ${r.adoszamok} adószám a központban.`); }
    catch (e: any) { this.hiba(e?.error?.error || 'A regisztráció nem sikerült.'); }
    finally { this.emailRegFut = false; }
  }
  get emailDomain() { return (this.emailBeall && this.emailBeall.email_domain) || 'naplo.cronomit.org'; }

  // Felismerés újrafuttatása egy dokumentumon (szöveg → ha szkennelt, OCR) – pl. e-mailes számlára.
  async felismeresUjra(d: any) {
    this.felismerFut = d.id;
    try {
      const r: any = await this.naplo.post('/dokumentum/' + d.id + '/felismeres', {});
      if (r?.ok) {
        await this.betolt();
        this.ok('Felismerés kész' + (r.ocr ? ' (OCR)' : '') + (r.partner?.nev ? ': ' + r.partner.nev : '.'));
      } else this.hiba(r?.error || 'Nem sikerült a felismerés.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba a felismerésnél.'); }
    finally { this.felismerFut = null; }
  }

  async betolt() {
    try { this.dokumentumok = await this.naplo.get('/dokumentum/' + this.aktiv.ugyfelId); }
    catch { this.dokumentumok = []; }
  }

  // a dokumentum (számla) éve: a PDF-ből kiolvasott dátumból, különben a számlaszámból/fájlnévből (pl. DP-2021-4)
  dokEv(d: any): number | null {
    if (d.szamla_ev) return +d.szamla_ev;
    const m = String(d.szamlaszam || d.fajl_nev || '').match(/(20\d{2})/);
    if (m) return +m[1];
    // végső esetben (pl. dátum nélküli e-mailes PDF) a feltöltés éve – hogy ne tűnjön el a szűrőből
    const f = String(d.feltoltve || '').match(/(20\d{2})/);
    return f ? +f[1] : null;
  }
  // a választható évek (a dokumentumokból + a könyvelt év), csökkenő sorrendben
  get evek(): number[] {
    const s = new Set<number>(); s.add(this.aktiv.ev);
    for (const d of this.dokumentumok) { const e = this.dokEv(d); if (e) s.add(e); }
    return [...s].sort((a, b) => b - a);
  }

  // Ékezet- és kisbetű-független összehasonlítás: „Arfolyam" és „árfolyam" is találjon,
  // mert a számláról kiolvasott partnernevek ékezetessége nem megbízható (OCR, PDF-szövegréteg).
  private static normal(s: any): string {
    return String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  // A keresés MINDEN azonosításra alkalmas mezőben fut: fájlnév, számlaszám, partner (+adószám),
  // és a párosított tétel adatai is – így a már lekönyvelt dokumentum is megtalálható.
  private illik(d: any): boolean {
    const k = DokumentumtarComponent.normal(this.kereses).trim();
    if (!k) return true;
    const szavak = k.split(/\s+/);
    const heystack = DokumentumtarComponent.normal([
      d.fajl_nev, d.szamlaszam, d.partner, d.partner_adoszam,
      d.tetel_szamlaszam, d.tetel_partner, d.szamla_datum,
    ].filter(Boolean).join(' '));
    return szavak.every((sz) => heystack.includes(sz));   // több szó = ÉS kapcsolat
  }

  // Év + szöveg szerint szűrt alaplista. A státusz-szegmens SZÁMLÁLÓI is ebből jönnek, különben
  // a fülön más szám állna, mint amennyi a táblában látszik.
  get alapLista() {
    let lista = this.dokumentumok;
    if (this.evSzuro !== 'mind') lista = lista.filter((d) => this.dokEv(d) === this.evSzuro);
    return lista.filter((d) => this.illik(d));
  }

  get szurt() {
    const lista = this.alapLista;
    if (this.szuro === 'parositott') return lista.filter((d) => d.parositva);
    if (this.szuro === 'parositlan') return lista.filter((d) => !d.parositva);
    return lista;
  }
  get osszesSzam() { return this.alapLista.length; }
  get parositlanSzam() { return this.alapLista.filter((d) => !d.parositva).length; }

  // ---- feltöltés (ZIP vagy egyedi PDF) ----
  async fajlValaszt(ev: any, zip: boolean) {
    const f = ev?.target?.files?.[0];
    if (!f || !this.aktiv.ugyfelId) return;
    const fd = new FormData();
    fd.append('file', f);
    fd.append('ev', String(this.aktiv.ev));
    this.feltoltes = true;
    try {
      if (zip) {
        const r: any = await this.naplo.post('/dokumentum/zip/' + this.aktiv.ugyfelId, fd);
        this.ok(`${r.osszes} PDF feltöltve · ${r.parositott} párosítva · ${r.parositlan} párosítatlan`);
      } else {
        const r: any = await this.naplo.post('/dokumentum/' + this.aktiv.ugyfelId, fd);
        this.ok(r.parositva ? 'Feltöltve és párosítva: ' + r.szamlaszam : 'Feltöltve (nincs automatikus párosítás).');
      }
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'A feltöltés nem sikerült.'); }
    finally { this.feltoltes = false; if (ev?.target) ev.target.value = ''; }
  }

  pdf(d: any) { this.data.megnyitFajl('/api/naplo/dokumentum/pdf/' + d.id, d?.nev || d?.tipus || 'Dokumentum'); }

  // ---- automatikus újrapárosítás a párosítatlanokra ----
  ujraParositFut = false;
  async ujraParosit() {
    if (!this.aktiv.ugyfelId) return;
    this.ujraParositFut = true;
    try {
      const r: any = await this.naplo.post('/dokumentum/' + this.aktiv.ugyfelId + '/ujraparosit', {});
      await this.betolt();
      this.ok(`Újrapárosítás kész. ${r?.parositott ?? 0} / ${r?.vizsgalt ?? 0} párosítva.`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült újrapárosítani.'); }
    finally { this.ujraParositFut = false; }
  }

  // ---- manuális párosítás ----
  async parosit(d: any) {
    this.parosDoc = d;
    // a fájlnév első tagja (kiterjesztés és az első _ / szóköz előtt) általában a számlaszám
    const alap = (d.fajl_nev || '').replace(/\.[a-z0-9]+$/i, '');
    this.tetelKeres = d.szamlaszam || alap.split(/[_\s]+/)[0] || alap;
    this.tetelTalalat = [];
    await this.tetelKereses();
    this.parosModal.present();
  }
  async tetelKereses() {
    try { this.tetelTalalat = await this.naplo.get('/dokumentum/' + this.aktiv.ugyfelId + '/tetelek' + (this.tetelKeres ? '?q=' + encodeURIComponent(this.tetelKeres) : '')); }
    catch { this.tetelTalalat = []; }
  }
  async valaszt(t: any) {
    try { await this.naplo.put('/dokumentum/' + this.parosDoc.id + '/parosit', { tetel_id: t.id || null, szamlaszam: t.szamlaszam }); this.parosModal.dismiss(); await this.betolt(); this.ok('Párosítva: ' + t.szamlaszam); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült párosítani.'); }
  }
  async levalaszt(d: any) {
    try { await this.naplo.del('/dokumentum/' + d.id + '/parosit'); await this.betolt(); this.ok('Párosítás megszüntetve.'); }
    catch { this.hiba('Nem sikerült leválasztani.'); }
  }
  async torol(d: any) {
    const a = await this.alert.create({ header: 'Törlés', message: (d.fajl_nev || 'A dokumentum') + ' törlése?', buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }] });
    await a.present(); const { role } = await a.onDidDismiss(); if (role !== 'destructive') return;
    try { await this.naplo.del('/dokumentum/' + d.id); await this.betolt(); } catch { this.hiba('Nem sikerült törölni.'); }
  }

  meret(b: number) { if (!b) return ''; return b > 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.round(b / 1024) + ' kB'; }
  // A PDF-ből kiolvasott partner címe (irsz település cím) – üres tagok nélkül.
  cim(d: any) { return [d.partner_irsz, d.partner_telepules, d.partner_cim].filter((x) => x).join(' '); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2200, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2800, color: 'danger' })).present(); }
}


